<template>
  <div class="image-carousel">
    <sc-text
      tag="h2"
      class="image-carousel__title"
      v-if="fields && fields.heading"
      :field="fields.heading"
    />
    <sc-rich-text
      tag="div"
      class="image-carousel__lead"
      v-if="fields && fields.leadCopy"
      :field="fields.leadCopy"
    />

    <div class="image-carousel__content">
      <template v-if="isEditing">
        <!--
          When experience editor is active we want to render all carousel slides, to simplify editing.
        -->
        <sc-placeholder name="jss-image-carousel" :rendering="rendering" />
      </template>
      <template v-else>
        <Splide ref="splide" v-if="hasImagePlaceholder" :options="options" aria-label="Carousel">
          <SplideSlide
            ref="SplideSlide"
            v-for="(image, index) in $options.computed.imagePlaceholder"
            :is="image"
            :key="`image${index}`"
          >
            <component :is="image" :key="`image${index}`" />
          </SplideSlide>
        </Splide>
      </template>
    </div>

    <sc-rich-text
      class="image-carousel__richtext"
      v-if="fields && fields.content"
      :field="fields.content"
    />
  </div>
</template>

<script>
import { Text, RichText, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { ref, reactive, shallowRef } from 'vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';

export default {
  name: 'CIS-Image-Carousel',
  components: {
    ScText: Text,
    ScRichText: RichText,
    ScPlaceholder: Placeholder,
    Splide,
    SplideSlide
  },
  props: {
    rendering: {
      type: Object
    },
    fields: {
      type: Object
    }
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-image-carousel',
          computedPropName: 'imagePlaceholder'
        }
      ]
    };
  },
  computed: {
    isEditing() {
      // this.$jss is defined on the Vue instance by the SitecoreJssPlugin and provides
      // reactive access to the `sitecoreContext` provided in layout service data.
      return this.$jss.sitecoreContext().pageEditing;
    },
    hasImagePlaceholder() {
      return this.$options?.computed?.imagePlaceholder?.length > 0;
    }
  },
  setup(props) {
    const splide = ref();
    const carouselSize = shallowRef(props?.rendering?.placeholders?.['jss-image-carousel']?.length);
    const slidesToShowDestop = shallowRef(props?.fields?.slidesToShowDestop?.value);
    const slidesToShowTablet = shallowRef(props?.fields?.slidesToShowTablet?.value);
    const slidesToShowMobile = shallowRef(props?.fields?.slidesToShowMobile?.value);

    // Destop - Slides to Show
    if (slidesToShowDestop?.value) {
      if (slidesToShowDestop?.value > carouselSize?.value)
        slidesToShowDestop.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 5) slidesToShowDestop.value = carouselSize?.value;
      else slidesToShowDestop.value = 5;
    }

    // Tablet - Slides to Show
    if (slidesToShowTablet?.value) {
      if (slidesToShowTablet?.value > carouselSize?.value)
        slidesToShowTablet.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 4) slidesToShowTablet.value = carouselSize?.value;
      else slidesToShowTablet.value = 4;
    }

    // Mobile - Slides to Show
    if (slidesToShowMobile?.value) {
      if (slidesToShowMobile?.value > carouselSize?.value)
        slidesToShowMobile.value = carouselSize?.value;
    } else {
      if (carouselSize?.value < 3) slidesToShowMobile.value = carouselSize?.value;
      else slidesToShowMobile.value = 3;
    }

    const options = reactive({
      type: 'loop',
      updateOnMove: true,
      arrows: carouselSize?.value <= slidesToShowDestop?.value ? false : true,
      rewind: true,
      pagination: false,
      perPage: slidesToShowDestop?.value,
      perMove: 1,
      autoplay:
        carouselSize?.value <= slidesToShowDestop?.value ? false : props?.fields?.autoplay?.value,
      drag: carouselSize?.value <= slidesToShowDestop?.value ? false : true,
      speed: props?.fields?.carouselSpeed?.value || 2000,
      interval: 4000,
      padding: { left: 34, right: 34 },
      breakpoints: {
        1024: {
          perPage: slidesToShowTablet?.value,
          arrows: carouselSize?.value <= slidesToShowTablet?.value ? false : true,
          autoplay:
            carouselSize?.value <= slidesToShowTablet?.value
              ? false
              : props?.fields?.autoplay?.value,
          drag: carouselSize?.value <= slidesToShowTablet?.value ? false : true
        },
        480: {
          perPage: slidesToShowMobile?.value,
          arrows: carouselSize?.value <= slidesToShowMobile?.value ? false : true,
          autoplay:
            carouselSize?.value <= slidesToShowMobile?.value
              ? false
              : props?.fields?.autoplay?.value,
          drag: carouselSize?.value <= slidesToShowMobile?.value ? false : true
        }
      }
    });

    return { splide, options };
  }
};
</script>
<style lang="scss">
.image-carousel {
  margin-top: 4rem;
  margin-bottom: 4rem;

  &__desc {
    padding: 1rem;

    p,
    a {
      font-size: 1.6rem;
      line-height: 1.8rem;

      @include desktop-up {
        font-size: 1.8rem;
        line-height: 2rem;
      }
    }
  }

  &__lead {
    margin-bottom: 3rem;
  }

  &__content {
    padding: 2rem 2rem 0 2rem;
  }

  &__richtext {
    padding-top: 4rem;
  }
}
</style>
